/**
 * <s-sunrise data-duration="3500"> — a small sun that rises from the bottom-left
 * corner and arcs to the top-right corner over about data-duration ms, then
 * fades out and removes itself. Never blocks taps or scrolling
 * (pointer-events: none), is hidden from screen readers, and does nothing for
 * shoppers who prefer reduced motion.
 */
class SSunrise extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.started) return;
    this.started = true;

    const duration = Number(this.dataset.duration) || 3500;
    const isSmall = window.matchMedia('(max-width: 767px)').matches;
    const size = isSmall ? 44 : 64;

    const layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:60;';

    // Small sun disc with a soft halo.
    const sun = document.createElement('div');
    sun.style.cssText = [
      'position:absolute',
      'left:0',
      'top:0',
      `width:${size}px`,
      `height:${size}px`,
      'border-radius:9999px',
      'background:radial-gradient(circle at 45% 40%, #fff6cf 0%, #ffd166 50%, #ff9f43 100%)',
      'box-shadow:0 0 18px 6px rgba(255,190,80,0.55), 0 0 40px 16px rgba(255,160,70,0.25)',
      'will-change:transform,opacity',
    ].join(';');

    // Short rays around the sun.
    const rays = document.createElement('div');
    const raySize = size * 2;
    rays.style.cssText = [
      'position:absolute',
      'left:50%',
      'top:50%',
      `width:${raySize}px`,
      `height:${raySize}px`,
      `margin:${-raySize / 2}px 0 0 ${-raySize / 2}px`,
      'border-radius:9999px',
      'background:repeating-conic-gradient(rgba(255,190,80,0.55) 0deg 8deg, rgba(255,190,80,0) 8deg 30deg)',
      '-webkit-mask:radial-gradient(circle, transparent 34%, #000 36%, transparent 70%)',
      'mask:radial-gradient(circle, transparent 34%, #000 36%, transparent 70%)',
    ].join(';');
    sun.appendChild(rays);

    layer.appendChild(sun);
    this.appendChild(layer);

    // Arc path: just off-screen bottom-left, up through the middle, to the top-right corner.
    const endX = `calc(100vw - ${size + 12}px)`;
    sun.animate(
      [
        { transform: `translate(${-size}px, 100vh) scale(0.8)`, opacity: 0 },
        { opacity: 1, offset: 0.12 },
        { transform: 'translate(45vw, 30vh) scale(1)', offset: 0.55 },
        { transform: `translate(${endX}, 12px) scale(1.05)`, opacity: 1 },
      ],
      { duration, easing: 'cubic-bezier(0.25, 0.6, 0.35, 1)', fill: 'both' }
    );

    rays.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(120deg)' }], {
      duration: duration + 400,
      easing: 'linear',
      fill: 'both',
    });

    const fadeOut = layer.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 400,
      delay: duration,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards',
    });
    fadeOut.finished.then(() => layer.remove()).catch(() => layer.remove());
  }
}

if (!customElements.get('s-sunrise')) customElements.define('s-sunrise', SSunrise);
